// `@/lib/chat-url` compat shim — the SPA's implementation of the chat view's
// "swap the URL onto the session I just created" call (aliased in
// vite.config.ts + tsconfig.json, exactly like next/navigation and
// @clerk/nextjs).
//
// THE BUG THIS FIXES (dev-flip P0-2). apps/web/lib/chat-url.ts does
// `window.history.replaceState('/chat/<id>')`. Under Next that is inert: the
// URL changes and the mid-stream chat component stays mounted. Under TanStack
// Router it is NOT inert — the router observes history and re-matches, so '/'
// (HomeRoute) unmounted and '/chat/$sessionId' mounted a FRESH GenUIChat, which
// immediately refetched the session and raced the POST /api/chat that was still
// creating it: GET /api/sessions/<new> → 404 → "Couldn't load this
// conversation", while the turn completed correctly on the server.
//
// THE FIX, in two halves (this file is half one):
//   1. Tell the router about the URL change properly — a `replace` navigation,
//      so router state (and everything reading usePathname()) stays truthful —
//      and TAG it in history state as `chatMinted: <sessionId>`.
//   2. routes/chat-shell.tsx owns '/' and '/chat/$sessionId' with ONE GenUIChat
//      instance and reads that tag: a tagged swap adopts the id without
//      changing the React key or the `sessionId` prop, so the live component is
//      never remounted and never refetches. Any UNtagged change of the session
//      in the URL (sidebar link, new-chat, back/forward, cold load) is a real
//      navigation and does remount, as it must.
//
// Falls back to the native replaceState when the router isn't registered yet or
// the target isn't a SPA chat path (e.g. the '/labs/genui/<id>' prefix, which
// this SPA does not route).

// Structurally typed against the router rather than importing its full generic
// surface (which would make this module part of the route-tree type graph, i.e.
// a cycle). `navigate` is TanStack's NavigateFn; the options object is built
// below and validated at the one call site.
interface ChatRouter {
  navigate(opts: {
    to: string;
    params: { sessionId: string };
    replace: boolean;
    resetScroll: boolean;
    state: (prev: Record<string, unknown>) => Record<string, unknown>;
  }): unknown;
}

/** History-state key set on a mint swap; read by routes/chat-shell.tsx. */
export const CHAT_MINTED_STATE_KEY = 'chatMinted';

let router: ChatRouter | null = null;

/**
 * Called once from main.tsx after createRouter() (registration, not import —
 * importing the router here would be a cycle: router → routeTree → chat view →
 * this module). The cast is the price of breaking that cycle: TanStack's
 * NavigateFn is generic over the whole route tree, which is precisely the type
 * we must not depend on here.
 */
export function registerChatRouter(next: { navigate: (opts: never) => unknown }): void {
  router = next as unknown as ChatRouter;
}

function nativeReplace(url: string): void {
  try {
    window.history.replaceState(null, '', url);
  } catch {
    /* replaceState can throw in rare embedded contexts — non-fatal */
  }
}

export function replaceChatUrl(url: string, sessionId: string): void {
  if (!router || !url.startsWith('/chat/')) {
    nativeReplace(url);
    return;
  }
  void router.navigate({
    to: '/chat/$sessionId',
    params: { sessionId },
    replace: true,
    // The chat is mid-stream; a scroll reset here would yank the user away
    // from the tokens landing on screen.
    resetScroll: false,
    // Merge, don't clobber: TanStack keeps its own bookkeeping in history state.
    state: (prev) => ({ ...prev, [CHAT_MINTED_STATE_KEY]: sessionId }),
  });
}
